// ⬡B:aba_shared.ham_core:CODE:auth_plus_identity_v2:20260420⬡
// HAM authentication and identity lookup. A lookup can confirm that an account
// exists. Only an authenticated exchange can create a signed world session.

import { useEffect, useState, useCallback, useRef, createContext, useContext } from 'react';
import { resolveAnewApiBase } from '@aba/api-core';
import { lookupWorldIdentity } from './accountLookup.js';

export { lookupWorldIdentity } from './accountLookup.js';

const SUPABASE_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ABACIA_URL) || 'https://dnzwyufdzafcwnjaqbxs.supabase.co';

function getAnonKey() {
  return typeof import.meta !== 'undefined'
    ? (import.meta.env?.VITE_ABACIA_ANON_KEY || '')
    : '';
}

export async function exchangeTrustedSession(accessToken, options = {}) {
  const token = String(accessToken || '').trim();
  if (!token) throw new Error('authenticated session required');
  const request = options.fetchImpl || fetch;
  const baseUrl = resolveAnewApiBase({ baseUrl: options.baseUrl });
  const res = await request(`${baseUrl}/auth/advisor/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ access_token: token })
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.ok || !data.hamUid || !data.sessionToken) {
    const err = new Error('world session unavailable');
    err.detail = data || {};
    throw err;
  }
  return { hamUid: String(data.hamUid), sessionToken: String(data.sessionToken) };
}

function hasSupabaseAuth() {
  const k = getAnonKey();
  return !!k && !k.includes('placeholder');
}

const HamContext = createContext(null);
const LEGACY_DEV_IDENTITY_KEY = 'aba.ham.dev_identity.v1';

function clearLegacyDevIdentity() {
  try {
    localStorage.removeItem(LEGACY_DEV_IDENTITY_KEY);
  } catch {}
}

// Promise-based singleton — prevents duplicate GoTrueClient instances
// when React StrictMode or concurrent mounts call getSupabase() before
// the first createClient() completes.
let _supabasePromise = null;
async function getSupabase() {
  if (_supabasePromise) return _supabasePromise;
  if (!hasSupabaseAuth()) return null;
  _supabasePromise = (async () => {
    const { createClient: _create } = await import('@supabase/supabase-js');
    return _create(SUPABASE_URL, getAnonKey(), {
    auth: {
      persistSession:    true,
      autoRefreshToken:  true,
      detectSessionInUrl: true,
      flowType: 'implicit'  // PKCE loses code_verifier on page reload → loop. Implicit uses URL hash, no localStorage dependency.
    }
  });
  })();
  return _supabasePromise;
}

// Sign-in continuity (Temp K-Dot's KDOT-ORIGINAL-TEMPI-CALLBACK-SESSION-REPAIR-20261019; Temp I, 10/19). Google
// sign-in on IRIS kept returning to "Sign in". Four plain pieces, each run by the tests from this source:
//   hasAuthCallback / consumeAuthCallback: a returning sign-in in the URL is read by the client before App clears
//     the URL or leaves the callback page;
//   sessionIsValid / beginGoogleSignIn: a valid same-account session is reused instead of signed out and sent
//     round Google again; an expired one, an explicit switch or an unrecognized account still goes to Google;
//     a world that fails to resolve is said, never treated as a missing identity;
//   resolutionFence: a late resolution from an account that has since changed, or after unmount, is dropped.
// The server contract is unchanged: only /auth/advisor/resolve's signed hamUid and sessionToken make a world.
export function hasAuthCallback(hash) {
  return /(^|[#&])(access_token|refresh_token|error|error_description)=/.test(String(hash || ''));
}

export async function consumeAuthCallback(options = {}) {
  const getClient = options.getClient || getSupabase;
  const supabase = await getClient();
  if (!supabase) return null;
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session || null;
  } catch {
    return null;
  }
}

export function sessionIsValid(session, nowMs) {
  const expires = Number(session?.expires_at);
  return !!(session?.user && session?.access_token) && Number.isFinite(expires) && expires * 1000 > nowMs + 30000;
}

export async function beginGoogleSignIn({ supabase, switchAccount = false, nowMs, reuse, storage, href, origin,
  isCurrent = () => true, signOutLocal }) {
  if (!isCurrent()) return 'cancelled';
  let existing = null;
  try {
    existing = (await supabase.auth.getSession())?.data?.session || null;
  } catch {}
  if (!isCurrent()) return 'cancelled';
  if (!switchAccount && sessionIsValid(existing, nowMs)) {
    try {
      await reuse(existing);
      return isCurrent() ? 'reused' : 'cancelled';
    } catch (e) {
      if (!isCurrent()) return 'cancelled';
      // An account her world does not know needs another account; any other failure is said, never looped.
      if (!['no_match', 'no_world_is_attached_to_that_email'].includes(e?.detail?.reason)) throw e;
      switchAccount = true;
    }
  }
  const curr = String(href || '').split('#')[0].split('?')[0];
  const skipStore = ['/join', '/meet', '/welcome', '/'].some(p => curr.endsWith(p));
  if (!skipStore) storage.setItem('auth_return_to', curr);
  // A stale or switched-away session is cleared locally first; a valid one was reused above.
  if (switchAccount || existing) {
    try {
      if (signOutLocal) await signOutLocal();
      else await supabase.auth.signOut({ scope: 'local' });
    } catch (_) {}
  }
  if (!isCurrent()) return 'cancelled';
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      // ⬡B:aba_shared.ham_core:FIX:redirect_same_origin:20260614⬡
      // Always redirect back to current origin so the session stays in same-origin localStorage.
      redirectTo: origin
    }
  });
  if (!isCurrent()) return 'cancelled';
  if (error) throw error;
  return 'redirected';
}

export function resolutionFence() {
  let generation = 0;
  let live = true;
  return {
    begin() { generation += 1; return generation; },
    current(gen) { return live && gen === generation; },
    stop() { live = false; }
  };
}

export function HamProvider({ children }) {
  const [ham, setHam] = useState(null);
  const [session, setSession] = useState(null);
  const [hamSessionToken, setHamSessionToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // ⬡B:aba_shared.ham_core:CODE:unrecognized_account_gate:20260529⬡
  // True when a Supabase session exists but the backend resolves no HAM
  // (no_match). Lets the UI show a "we don't recognize this account" screen
  // instead of silently dropping back to sign-in and looping.
  const [unrecognized, setUnrecognized] = useState(false);
  // A Google identity is present but her world did not resolve (not "no such account"): the page can say so.
  const [worldError, setWorldError] = useState(null);
  // K-Dot's KD-SIGNIN-01: ONE lifetime and account generation for every writer of the signed identity -- the first
  // read, auth events, an explicit reuse, sign-out and unmount. Each starts a new generation, and only the newest
  // live one may write, so a late world for an account that has since changed, signed out or unmounted is dropped.
  const fenceRef = useRef(null);
  // A current operation may deliberately clear the SDK session before choosing another account. Its own
  // SIGNED_OUT event belongs to that same intent; any newer account, public intent or unmount still wins.
  const expectedSignOutRef = useRef(null);
  // ⬡B:aba_shared.ham_core:CODE:gotrueclient_single_instance:20260521⬡
  // Expose the singleton Supabase client via context so consumers (e.g. createAbaClient)
  // can reuse the same instance rather than spawning a second GoTrueClient.
  const [supabaseClient, setSupabaseClient] = useState(null);
  const [sessionId, setSessionId] = useState(() =>
    `ccwa.${Date.now()}.${Math.random().toString(36).slice(2, 8)}`
  );

  const resolveViaBackend = useCallback((hint) => lookupWorldIdentity(hint), []);

  const resolveAuthenticatedUser = useCallback(async (authSession) => {
    const email = authSession?.user?.email;
    const trusted = await exchangeTrustedSession(authSession?.access_token);
    return {
      ham: { acl_uid: trusted.hamUid, email_primary: email || null },
      sessionToken: trusted.sessionToken
    };
  }, []);

  const signOutClient = useCallback(async (supabase, fence, gen, options) => {
    if (fenceRef.current !== fence || !fence.current(gen)) return;
    const pending = { fence, gen };
    expectedSignOutRef.current = pending;
    try {
      return await supabase.auth.signOut(options);
    } finally {
      if (expectedSignOutRef.current === pending) expectedSignOutRef.current = null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const fence = resolutionFence();
    fenceRef.current = fence;
    let subscription = null;
    const failed = (e) => {
      const unknown = ['no_match', 'no_world_is_attached_to_that_email'].includes(e?.detail?.reason);
      setHam(null);
      setHamSessionToken(null);
      setUnrecognized(unknown);
      setWorldError(unknown ? null : 'world_session_unavailable');
    };

    async function init() {
      clearLegacyDevIdentity();
      try {
        var urlHam = (typeof window !== 'undefined' && window.__ANU_HAM) ? String(window.__ANU_HAM) : '';
        if (urlHam) {
          var cleanHam = urlHam.replace(/[^A-Za-z0-9_-]/g, '').toUpperCase();
          var injectedSession = String(window.__ANU_SESSION || '').trim();
          if (cleanHam && injectedSession && mounted) {
            setHam({ acl_uid: cleanHam });
            setHamSessionToken(injectedSession);
            setUnrecognized(false);
            setLoading(false);
            return;
          }
        }
      } catch (eUrlHam) { /* fall through to normal auth */ }
      if (hasSupabaseAuth()) {
        try {
          const supabase = await getSupabase();
          // ⬡B:aba_shared.ham_core:CODE:gotrueclient_single_instance:20260521⬡
          // Stash the resolved singleton so it can be passed out via context.
          if (mounted) setSupabaseClient(supabase);
          // The listener is attached BEFORE the first world resolution is awaited (KD-SIGNIN-01 A).
          const { data: listening } = supabase.auth.onAuthStateChange(async (_evt, newSession) => {
            if (!mounted) return;
            const ownSignOut = expectedSignOutRef.current;
            const gen = _evt === 'SIGNED_OUT' && !newSession && ownSignOut?.fence === fence
              && fence.current(ownSignOut.gen) ? ownSignOut.gen : fence.begin();
            setSession(newSession);
            if (newSession?.user) {
              try {
                const r = await resolveAuthenticatedUser(newSession);
                if (!fence.current(gen)) return;
                setHam(r.ham); setHamSessionToken(r.sessionToken); setUnrecognized(false); setWorldError(null);
              } catch (e) {
                console.error(e);
                if (fence.current(gen)) failed(e);
              }
            } else {
              setHam(null); setHamSessionToken(null); setUnrecognized(false); setWorldError(null);
            }
          });
          subscription = listening?.subscription || null;
          if (!mounted && subscription) subscription.unsubscribe();
          const gen = fence.begin();
          const { data } = await supabase.auth.getSession();
          if (fence.current(gen)) setSession(data.session);
          if (fence.current(gen) && data.session?.user) {
            try {
              const resolved = await resolveAuthenticatedUser(data.session);
              if (fence.current(gen)) {
                setHam(resolved.ham);
                setHamSessionToken(resolved.sessionToken);
                setUnrecognized(false);
                setWorldError(null);
              }
            } catch (e) {
              console.error('ham-core: resolve failed', e);
              if (fence.current(gen)) failed(e);
            }
          }
        } catch (e) {
          console.warn('ham-core: authenticated sign in unavailable', e);
        }
      }
      if (mounted) setLoading(false);
    }

    init();
    return () => {
      mounted = false;
      fence.stop();
      if (subscription) subscription.unsubscribe();
      if (fenceRef.current === fence) fenceRef.current = null;
      if (expectedSignOutRef.current?.fence === fence) expectedSignOutRef.current = null;
    };
  }, [resolveAuthenticatedUser]);

  // signInWithGoogle({ switchAccount: true }) is the explicit other-account path; a plain call reuses a valid session.
  const signInWithGoogle = useCallback(async (options) => {
    // KD-SIGNIN-02: capture intent BEFORE client acquisition or the SDK's asynchronous session read.
    const fence = fenceRef.current;
    if (!fence) return 'cancelled';
    const gen = fence.begin();
    const isCurrent = () => fenceRef.current === fence && fence.current(gen);
    const supabase = await getSupabase();
    if (!isCurrent()) return 'cancelled';
    if (!supabase) throw new Error('Supabase auth not configured.');
    return beginGoogleSignIn({
      supabase,
      switchAccount: !!(options && options.switchAccount === true),
      nowMs: Date.now(),
      storage: sessionStorage,
      href: window.location.href,
      origin: window.location.origin,
      isCurrent,
      signOutLocal: () => signOutClient(supabase, fence, gen, { scope: 'local' }),
      reuse: async (existing) => {
        if (!isCurrent()) return;
        try {
          const r = await resolveAuthenticatedUser(existing);
          if (!isCurrent()) return;
          setSession(existing);
          setHam(r.ham); setHamSessionToken(r.sessionToken); setUnrecognized(false); setWorldError(null);
        } catch (e) {
          if (!isCurrent()) return;
          const unknown = ['no_match', 'no_world_is_attached_to_that_email'].includes(e?.detail?.reason);
          setHam(null); setHamSessionToken(null); setUnrecognized(unknown);
          setWorldError(unknown ? null : 'world_session_unavailable');
          throw e;
        }
      }
    });
  }, [resolveAuthenticatedUser, signOutClient]);

  const signInWithEmailLink = useCallback(async (email) => {
    const supabase = await getSupabase();
    if (!supabase) throw new Error('Supabase auth not configured.');
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // ⬡B:aba_shared.ham_core:FIX:redirect_same_origin:20260614⬡
        emailRedirectTo: window.location.origin
      }
    });
    if (error) throw error;
  }, []);

  const lookUpAccount = useCallback(async ({ email, aclUid, slug } = {}) => {
    const hint = {};
    if (aclUid) hint.aclUid = aclUid;
    else if (email) hint.email = email;
    else if (slug) hint.slug = slug;
    else throw new Error('account lookup needs email or account code');
    return resolveViaBackend(hint);
  }, [resolveViaBackend]);

  const signOut = useCallback(async () => {
    // KD-SIGNIN-03: a later account or unmount invalidates this completion, just as it invalidates reuse.
    const fence = fenceRef.current;
    if (!fence) return;
    const gen = fence.begin();
    const isCurrent = () => fenceRef.current === fence && fence.current(gen);
    clearLegacyDevIdentity();
    if (hasSupabaseAuth()) {
      const supabase = await getSupabase();
      if (!isCurrent()) return;
      if (supabase) await signOutClient(supabase, fence, gen);
    }
    if (!isCurrent()) return;
    setSession(null);
    setHam(null);
    setHamSessionToken(null);
    setUnrecognized(false);
    setWorldError(null);
  }, [signOutClient]);

  const newThread = useCallback(() => {
    setSessionId(`ccwa.${Date.now()}.${Math.random().toString(36).slice(2, 8)}`);
  }, []);

  const value = {
    ham,
    session,
    hamSessionToken,
    loading,
    sessionId,
    newThread,
    signInWithGoogle,
    signInWithEmailLink,
    lookUpAccount,
    signOut,
    unrecognized,
    worldError,
    hasAuth: hasSupabaseAuth(),
    hamHint: ham ? { aclUid: ham.acl_uid } : null,
    // ⬡B:aba_shared.ham_core:FIX:expose_supabase_client:20260706⬡
    // span.task D.5 (presence/idle indicators). The real, already-resolved
    // Supabase client singleton was captured in local state (line ~85) but
    // never actually passed out through the context, so nothing outside this
    // provider could use it for anything real-time. Exposing it here, purely
    // additive, no existing consumer's shape changes.
    supabaseClient
  };

  return <HamContext.Provider value={value}>{children}</HamContext.Provider>;
}

export function useHam() {
  const ctx = useContext(HamContext);
  if (!ctx) throw new Error('useHam: must be called inside <HamProvider>');
  return ctx;
}

export function useHamGuard() {
  const { ham, hamSessionToken, loading, unrecognized, worldError, signInWithGoogle, signInWithEmailLink, lookUpAccount, hasAuth } = useHam();
  return {
    ham,
    hamSessionToken,
    ready: !loading && !!ham && !!hamSessionToken,
    loading,
    unrecognized,
    worldError,
    hasAuth,
    signInWithGoogle,
    signInWithEmailLink,
    lookUpAccount
  };
}

// ⬡B:DC499D0C:FIX:sign_in_never_a_dead_button:20260801⬡
// TM-9. The three functions above reject when a door cannot open. Until 20260801
// every caller invoked them straight out of an onClick with no catch, so the
// rejection went nowhere and the click produced nothing a person could see.
// ./signInMessages.js turns any such failure into one sentence written for the
// person standing at the door. Re-exported here so every consumer that already
// imports '@aba/ham-core' gets it without a second import path.
export {
  SIGN_IN_MESSAGES,
  SIGN_IN_FORBIDDEN_WORDS,
  ACCOUNT_LOOKUP_PLACEHOLDER,
  ACCOUNT_LOOKUP_ACTION,
  ACCOUNT_LOOKUP_BUSY,
  SIGN_IN_BUSY,
  ACCOUNT_LOOKUP_HELP,
  ACCOUNT_LOOKUP_FOUND,
  ACCOUNT_LOOKUP_FOUND_NO_SIGN_IN,
  classifySignInFailure,
  signInFailureMessage,
  signInUnavailableNotice,
  accountLookupHint,
} from './signInMessages.js';
